import { useAuthStore } from "@/stores/authStore";
import { AppRole } from "@/types/database";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/services/LoggingService";

export function useUserRole() {
  const { user } = useAuthStore();
  
  const { data: roleData, isLoading } = useQuery({
    queryKey: ['user-role', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        return null;
      }
      
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', user.id)
        .single();
        
      if (profileError) {
        // Handle RLS policy errors gracefully - return default role instead of throwing
        if ((profileError as any).code === 'PGRST301' || profileError.message?.includes('multiple') || profileError.message?.includes('no rows')) {
          logger.warn('useUserRole', 'RLS policy blocking access, using default role', { 
            error: profileError.message, 
            userId: user.id 
          });
          return { role: 'investor' as AppRole };
        }
        
        // For other errors, log but don't spam
        logger.warn('useUserRole', 'Profile access failed, using default role', { 
          error: profileError.message, 
          userId: user.id 
        });
        return { role: 'investor' as AppRole };
      }

      return userProfile;
    },
    enabled: !!user?.id,
    retry: (failureCount, error) => {
      // Don't retry on RLS policy errors or access issues
      if ((error as any)?.code === 'PGRST301' || error?.message?.includes('multiple') || error?.message?.includes('no rows')) {
        return false;
      }
      return failureCount < 1; // Only retry once for other errors
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes to reduce API calls
    refetchOnWindowFocus: false // Don't refetch when window regains focus
  });

  // Always provide a valid role - default to 'investor' for regular users
  const currentRole = (roleData?.role || 'investor') as AppRole;

  const hasRole = async (requiredRole?: AppRole) => {
    const shouldDebugRoles = typeof window !== 'undefined' && (
      window.location.hostname === 'localhost' ||
      window.location.hostname.includes('127.0.0.1') ||
      (window as any).__DEBUG_ROLES__
    );

    if (shouldDebugRoles) {
      console.log("[Role] Checking role access:", {
        userId: user?.id,
        requiredRole,
        currentRole,
        timestamp: new Date().toISOString()
      });
    }

    if (!requiredRole) return true;
    if (!user?.id) return false;

    // Role hierarchy check
    switch (requiredRole) {
      case 'investor':
        return true;  // Base role - everyone has access
      case 'advisor':
        return (
          currentRole === 'advisor' ||
          currentRole === 'manager' ||
          currentRole === 'manager_admin' ||
          currentRole === 'sys_admin'
        );
      case 'manager':
        // Managers and above (manager_admin, sys_admin)
        return (
          currentRole === 'manager' ||
          currentRole === 'manager_admin' ||
          currentRole === 'sys_admin'
        );
      case 'manager_admin':
        return currentRole === 'manager_admin' || currentRole === 'sys_admin';
      case 'sys_admin':
        return currentRole === 'sys_admin';
      default:
        return false;
    }
  };

  return {
    userRole: currentRole,
    hasRole,
    isLoading
  };
}
